import { useEffect, type FormEvent, type ReactNode } from "react";
import { useLang } from "../i18n";
import { Icon } from "./Icon";

/** The prototype's dialog frame (.modal-back / .modal) with Escape to close. */
export function Modal({ title, sub, wide, onClose, onSubmit, alert, children, saveLabel, busy, extra, saveDisabled, saveTestId, danger, testId, cancelLabel }: {
  title: string; sub?: string; wide?: boolean; onClose(): void; onSubmit(e: FormEvent): void; alert?: string | null; children: ReactNode; saveLabel?: string; busy?: boolean;
  /** More buttons beside Save (a second way to submit, for example). */
  extra?: ReactNode;
  /** Save stays disabled until what the dialog asks for is given (a reason, a confirmation). */
  saveDisabled?: boolean; saveTestId?: string; danger?: boolean; testId?: string;
  /** The first button's label when nothing is left to cancel (a result shown after the action). */
  cancelLabel?: string;
}) {
  const { t } = useLang();
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="modal-back open" role="dialog" aria-modal="true" aria-label={title}>
      <form className={`modal ${wide ? "wide" : ""}`} data-testid={testId} noValidate onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); onSubmit(e); }}>
        <div className="modal-head"><div><h3>{title}</h3>{sub && <div className="hint">{sub}</div>}</div>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label={t("close")}><Icon name="x" /></button></div>
        <div className="modal-body">{children}</div>
        <div className={`dlg-alert ${alert ? "show" : ""}`} role="alert">{alert && <><Icon name="alert" /><span>{alert}</span></>}</div>
        <div className="modal-foot">
          <button type="button" className="btn" onClick={onClose}>{cancelLabel ?? t("cancel")}</button>
          {extra}
          <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} type="submit" disabled={busy || saveDisabled} data-testid={saveTestId}>{busy ? t("saving") : saveLabel ?? t("save")}</button>
        </div>
      </form>
    </div>
  );
}

export function Confirm({ title, body, okLabel, onOk, onClose }: { title: string; body: string; okLabel: string; onOk(): void; onClose(): void }) {
  const { t } = useLang();
  return (
    <div className="modal-back open" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal confirm">
        <div className="modal-body one"><span className="cf-ico danger"><Icon name="trash" /></span><div><h3>{title}</h3><p>{body}</p></div></div>
        <div className="modal-foot"><button type="button" className="btn" onClick={onClose}>{t("no")}</button><button type="button" className="btn btn-danger" autoFocus onClick={onOk}>{okLabel}</button></div>
      </div>
    </div>
  );
}

export function Field({ label, req, full, error, hint, htmlFor, children }: { label?: string; req?: boolean; full?: boolean; error?: string; hint?: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className={`field ${full ? "full" : ""} ${error ? "invalid" : ""}`}>
      {label && <label htmlFor={htmlFor} className={req ? "req" : ""}>{label}</label>}
      {children}
      <div className="ferr" role="alert">{error}</div>
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

/** A delete the rules do not allow (records in use): an information dialog with a lock and one OK button. */
export function Blocked({ title, body, onClose }: { title: string; body: string; onClose(): void }) {
  const { t } = useLang();
  return (
    <div className="modal-back open" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal confirm">
        <div className="modal-body one"><span className="cf-ico"><Icon name="lock" /></span><div><h3>{title}</h3><p>{body}</p></div></div>
        <div className="modal-foot"><button type="button" className="btn btn-primary" autoFocus onClick={onClose}>{t("ok")}</button></div>
      </div>
    </div>
  );
}

/** A dialog that only shows something (no form): a title, its content and Close. Escape closes it. */
export function ViewDialog({ title, wide, onClose, children, testId }: { title: string; wide?: boolean; onClose(): void; children: ReactNode; testId?: string }) {
  const { t } = useLang();
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="modal-back open" role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
      <div className={`modal ${wide ? "wide" : ""}`}>
        <div className="modal-head"><div><h3>{title}</h3></div>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label={t("close")}><Icon name="x" /></button></div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot"><button type="button" className="btn" onClick={onClose}>{t("close")}</button></div>
      </div>
    </div>
  );
}
